import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { type AddMemberToGroupModalRef } from '../components/admin/AddMemberToGroupModal';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import { useGetGroup, useRemoveMembersFromGroup } from '../services/groups.service';
import { useResendInvite } from '../services/users.service';
import { queryClient } from '../lib/queryClient';
import { useDebouncedValue } from './useDebouncedValue';
import { isAdmin } from '../utils/permissions';
import { USER_PER_PAGE } from '../constants/userTable';

export const useGroupDetailController = () => {
  const { t } = useTranslation();
  const { groupId } = useParams<{ groupId: string }>();
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const companies = useUserCompaniesStore((state) => state.companies);
  const currentUserRole = companies.find((c) => c.id === selectedCompanyId)?.role;
  const canManage = !!currentUserRole && isAdmin(currentUserRole);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const addMemberModalRef = useRef<AddMemberToGroupModalRef>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const {
    data: groupData,
    isLoading: isGroupLoading,
    error: groupError,
    refetch,
  } = useGetGroup(selectedCompanyId || '', groupId || '');

  const resendInviteMutation = useResendInvite(selectedCompanyId || '');
  const removeMembersMutation = useRemoveMembersFromGroup(selectedCompanyId || '');

  const handleRoleFilterChange = (role: string) => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  const handleUserAction = (userId: string, action: 'edit' | 'remove' | 'resend') => {
    if (action === 'resend') {
      resendInviteMutation.mutate(userId);
    } else if (action === 'remove' && groupId) {
      removeMembersMutation.mutate({ groupId, memberIds: [userId] });
    }
  };

  const handleBulkResend = (userIds: string[]) => {
    userIds.forEach((userId) => resendInviteMutation.mutate(userId));
  };

  const handleBulkRemove = (userIds: string[]) => {
    if (groupId) {
      removeMembersMutation.mutate({ groupId, memberIds: userIds });
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries();
    toast.success(t('admin.group_detail.refresh_success'));
  };

  const handleAddMember = () => {
    addMemberModalRef.current?.open();
  };

  const handleMembersAdded = () => {
    refetch();
  };

  const filteredMembers =
    groupData?.members.filter((member) => {
      const matchesSearch =
        !debouncedSearch ||
        member.firstName.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        member.lastName.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        member.email.toLowerCase().includes(debouncedSearch.toLowerCase());

      const matchesRole = !roleFilter || member.role === roleFilter;

      return matchesSearch && matchesRole;
    }) || [];

  const totalPages = Math.ceil(filteredMembers.length / USER_PER_PAGE);
  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * USER_PER_PAGE,
    currentPage * USER_PER_PAGE,
  );

  return {
    selectedCompanyId,
    groupId,
    canManage,
    groupData,
    isGroupLoading,
    groupError,
    search,
    setSearch,
    roleFilter,
    handleRoleFilterChange,
    currentPage,
    setCurrentPage,
    paginatedMembers,
    totalPages,
    addMemberModalRef,
    handleUserAction,
    handleBulkResend,
    handleBulkRemove,
    handleRefresh,
    handleAddMember,
    handleMembersAdded,
  };
};
